import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../../api/axios';
import { useAuth } from '../../context/AuthContext';
import AdminSearch from '../../components/admin/AdminSearch';

const statusStyle = {
  new: 'bg-amber-100 text-amber-800',
  in_progress: 'bg-blue-100 text-blue-800',
  resolved: 'bg-emerald-100 text-emerald-800',
};

export default function AdminLeads() {
  const { hasPerm } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState({ items: [], pages: 1, total: 0, newCount: 0 });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const search = searchParams.get('search') || '';
  const status = searchParams.get('status') || '';
  const page = Number(searchParams.get('page') || 1);
  const [searchInput, setSearchInput] = useState(search);

  const fetchLeads = useCallback(() => {
    setLoading(true);
    const params = { page, limit: 20 };
    if (search) params.search = search;
    if (status) params.status = status;
    api.get('/leads', { params })
      .then((res) => {
        setData(res.data);
      })
      .catch(() => toast.error('Could not load leads'))
      .finally(() => setLoading(false));
  }, [page, search, status]);

  useEffect(fetchLeads, [fetchLeads]);

  const applySearch = (value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set('search', value); else next.delete('search');
    next.set('page', '1');
    setSearchParams(next);
  };

  const applyStatus = (value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set('status', value); else next.delete('status');
    next.set('page', '1');
    setSearchParams(next);
  };

  const openLead = (lead) => {
    setSelected(lead);
    setAdminNotes(lead.adminNotes || '');
  };

  const updateLead = async (nextStatus = selected.status) => {
    setSaving(true);
    try {
      const { data: updated } = await api.patch(`/leads/${selected._id}`, { status: nextStatus, adminNotes });
      setSelected(updated);
      setAdminNotes(updated.adminNotes || '');
      toast.success('Lead updated');
      window.dispatchEvent(new Event('leads:changed'));
      fetchLeads();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not update lead');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (lead) => {
    if (!window.confirm(`Delete the lead from ${lead.name}?`)) return;
    try {
      await api.delete(`/leads/${lead._id}`);
      if (selected?._id === lead._id) setSelected(null);
      toast.success('Lead deleted');
      window.dispatchEvent(new Event('leads:changed'));
      fetchLeads();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not delete lead');
    }
  };

  return (
    <div className="space-y-6 text-[#2B1810]">
      <header className="border border-[#E8DFD8] bg-white p-6 shadow-sm">
        <div className="flex flex-col justify-between gap-4 xl:flex-row xl:items-center">
          <div><p className="text-[9px] font-bold uppercase text-[#9D772D]">Customer enquiries</p><h1 className="mt-1 font-heading text-2xl font-bold">Leads and Messages</h1><p className="mt-1 text-xs text-[#8A796C]">{data.total} messages · {data.newCount} awaiting review</p></div>
          <div className="flex w-full flex-col gap-3 sm:flex-row xl:w-auto">
            <AdminSearch value={searchInput} onChange={setSearchInput} onSearch={applySearch} placeholder="Name, email, phone, order or message" />
            <select value={status} onChange={(event) => applyStatus(event.target.value)} className="h-10 border border-[#E0D6CC] bg-white px-3 text-xs font-semibold outline-none focus:border-[#C49A45]"><option value="">All statuses</option><option value="new">New</option><option value="in_progress">In progress</option><option value="resolved">Resolved</option></select>
          </div>
        </div>
      </header>

      {selected && (
        <section className="border border-[#D9D0C8] bg-[#FAF9F7] p-5 shadow-sm sm:p-6">
          <div className="flex items-start justify-between gap-4 border-b border-[#D9D0C8] pb-4"><div><div className="flex flex-wrap items-center gap-2"><h2 className="font-heading text-xl font-bold">{selected.topic}</h2><span className={`px-2 py-1 text-[9px] font-bold uppercase ${statusStyle[selected.status]}`}>{selected.status.replace('_', ' ')}</span></div><p className="mt-2 text-xs text-[#76675E]">Received {new Date(selected.createdAt).toLocaleString('en-IN')}</p></div><button type="button" onClick={() => setSelected(null)} className="h-9 w-9 text-[#8A796C] hover:text-red-700" aria-label="Close lead"><svg viewBox="0 0 24 24" className="h-5 w-5 mx-auto" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 6l12 12M18 6 6 18"/></svg></button></div>
          <div className="mt-5 grid gap-6 lg:grid-cols-[1fr_320px]">
            <div><p className="whitespace-pre-wrap text-sm leading-7 text-[#40352F]">{selected.message}</p>{selected.orderNumber && <p className="mt-5 border-l-2 border-[#C49A45] pl-3 text-xs font-semibold">Order reference: {selected.orderNumber}</p>}</div>
            <aside className="border-t border-[#D9D0C8] pt-5 lg:border-l lg:border-t-0 lg:pl-6 lg:pt-0"><p className="font-bold">{selected.name}</p><a href={`mailto:${selected.email}`} className="mt-2 block break-all text-xs text-[#7F5D1E] hover:underline">{selected.email}</a>{selected.phone && <a href={`tel:${selected.phone}`} className="mt-2 block text-xs text-[#7F5D1E] hover:underline">{selected.phone}</a>}<p className="mt-5 text-[9px] font-bold uppercase text-[#76675E]">Internal notes</p><textarea rows="5" value={adminNotes} onChange={(event) => setAdminNotes(event.target.value)} disabled={!hasPerm('leads', 'update')} className="input-field mt-2 resize-y bg-white text-xs" placeholder="Follow-up details, owner, outcome..."/><div className="mt-3 flex flex-wrap gap-2">{hasPerm('leads', 'update') && <><select value={selected.status} onChange={(event) => setSelected((current) => ({ ...current, status: event.target.value }))} className="input-field min-w-[130px] flex-1 py-2 text-xs"><option value="new">New</option><option value="in_progress">In progress</option><option value="resolved">Resolved</option></select><button type="button" disabled={saving} onClick={() => updateLead(selected.status)} className="bg-[#2B1810] px-4 py-2 text-[10px] font-bold uppercase text-white disabled:opacity-60">{saving ? 'Saving...' : 'Save'}</button></>}</div>{selected.handledBy?.name && <p className="mt-3 text-[10px] text-[#8A796C]">Last handled by {selected.handledBy.name}</p>}</aside>
          </div>
        </section>
      )}

      <div className="overflow-x-auto border border-[#E8DFD8] bg-white shadow-sm">
        <table className="w-full min-w-[820px] text-left text-xs">
          <thead className="border-b border-[#E8DFD8] bg-[#F5EFEB] text-[10px] uppercase"><tr><th className="p-4">Contact</th><th className="p-4">Topic and message</th><th className="p-4">Order</th><th className="p-4">Received</th><th className="p-4">Status</th><th className="p-4 text-right">Actions</th></tr></thead>
          <tbody className="divide-y divide-[#E8DFD8]">
            {loading ? <tr><td colSpan="6" className="p-8 text-center text-[#8A796C]">Loading leads...</td></tr> : data.items.length === 0 ? <tr><td colSpan="6" className="p-8 text-center text-[#8A796C]">No contact messages found.</td></tr> : data.items.map((lead) => <tr key={lead._id} className={lead.status === 'new' ? 'bg-amber-50/40' : 'hover:bg-[#FAF8F5]'}><td className="p-4"><p className="font-bold">{lead.name}</p><p className="mt-1 max-w-[180px] truncate text-[10px] text-[#8A796C]">{lead.email}</p>{lead.phone && <p className="mt-1 text-[10px] text-[#8A796C]">{lead.phone}</p>}</td><td className="p-4"><p className="font-semibold">{lead.topic}</p><p className="mt-1 max-w-[300px] truncate text-[10px] text-[#8A796C]">{lead.message}</p></td><td className="p-4 font-mono text-[10px]">{lead.orderNumber || '—'}</td><td className="p-4 text-[#76675E]">{new Date(lead.createdAt).toLocaleDateString('en-IN')}<p className="mt-1 text-[10px]">{new Date(lead.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</p></td><td className="p-4"><span className={`px-2 py-1 text-[9px] font-bold uppercase ${statusStyle[lead.status]}`}>{lead.status.replace('_', ' ')}</span></td><td className="p-4 text-right space-x-3"><button type="button" onClick={() => openLead(lead)} className="font-bold text-[#9D772D] hover:underline">View</button>{hasPerm('leads', 'delete') && <button type="button" onClick={() => remove(lead)} className="font-bold text-red-700 hover:underline">Delete</button>}</td></tr>)}
          </tbody>
        </table>
      </div>

      {data.pages > 1 && <div className="flex flex-wrap gap-2">{Array.from({ length: data.pages }, (_, index) => index + 1).map((pageNumber) => <button type="button" key={pageNumber} onClick={() => { const next = new URLSearchParams(searchParams); next.set('page', pageNumber); setSearchParams(next); }} className={`h-8 w-8 text-xs font-bold ${pageNumber === page ? 'bg-[#C49A45] text-white' : 'border border-[#D9D0C8] bg-white'}`}>{pageNumber}</button>)}</div>}
    </div>
  );
}
